import type {
	Address,
	AddressData,
	Addresses
} from '~~/core/entities/Addresses';

/**
 * Serviço centralizado para gerenciar endereços de forma escalável
 * Permite adicionar novos estados, macro-regiões e cidades sem alterar código em vários lugares
 */
export class AddressService {
	private static instance: AddressService;
	private addresses: Addresses[] = [];

	private constructor() {}

	static getInstance(): AddressService {
		if (!AddressService.instance) {
			AddressService.instance = new AddressService();
		}
		return AddressService.instance;
	}

	/**
	 * Inicializa o serviço com os dados de endereços
	 */
	setAddresses(addresses: Addresses[]): void {
		this.addresses = addresses;
	}

	/**
	 * Retorna todos os endereços
	 */
	getAllAddresses(): Addresses[] {
		return this.addresses;
	}

	/**
	 * Retorna endereços por estado
	 */
	getAddressesByState(state: string): Addresses | undefined {
		return this.addresses.find(addr => addr.state === state);
	}

	/**
	 * Retorna todos os estados disponíveis
	 */
	getAllStates(): string[] {
		return this.addresses.map(addr => addr.state);
	}

	/**
	 * Retorna macro-regiões por estado
	 */
	getMacroregionsByState(state: string): Address[] {
		const stateAddresses = this.getAddressesByState(state);
		return stateAddresses?.macroregions || [];
	}

	/**
	 * Retorna cidades por estado
	 */
	getCitiesByState(state: string): Address[] {
		const stateAddresses = this.getAddressesByState(state);
		return stateAddresses?.cities || [];
	}

	/**
	 * Retorna regiões por estado
	 */
	getRegionsByState(state: string): AddressData[] {
		const stateAddresses = this.getAddressesByState(state);
		return stateAddresses?.regions || [];
	}

	/**
	 * Retorna regiões por cidade
	 */
	getRegionsByCity(state: string, city: string): AddressData[] {
		const regions = this.getRegionsByState(state);
		return regions.filter(region => region.data.city === city);
	}

	/**
	 * Retorna regiões por macro-região
	 */
	getRegionsByMacroregion(state: string, macroregion: string): AddressData[] {
		const regions = this.getRegionsByState(state);
		return regions.filter(region => region.data.macroregion === macroregion);
	}

	/**
	 * Retorna o estado de uma cidade específica
	 */
	getStateByCity(city: string): string | undefined {
		for (const address of this.addresses) {
			const cityExists = address.cities.some(c => c.label === city);
			if (cityExists) {
				return address.state;
			}
		}
		return undefined;
	}

	/**
	 * Retorna a macro-região de uma cidade específica
	 */
	getMacroregionByCity(state: string, city: string): string | undefined {
		const regions = this.getRegionsByCity(state, city);
		return regions[0]?.data.macroregion;
	}

	/**
	 * Verifica se uma cidade existe em um estado
	 */
	hasCityInState(state: string, city: string): boolean {
		const cities = this.getCitiesByState(state);
		return cities.some(c => c.label === city);
	}

	/**
	 * Verifica se uma macro-região existe em um estado
	 */
	hasMacroregionInState(state: string, macroregion: string): boolean {
		const macroregions = this.getMacroregionsByState(state);
		return macroregions.some(m => m.label === macroregion);
	}

	/**
	 * Retorna informações completas de uma região
	 */
	getRegionInfo(state: string, regionLabel: string): AddressData | undefined {
		const regions = this.getRegionsByState(state);
		return regions.find(region => region.label === regionLabel);
	}

	/**
	 * Retorna todas as cidades de uma macro-região
	 */
	getCitiesByMacroregion(state: string, macroregion: string): Address[] {
		const regions = this.getRegionsByMacroregion(state, macroregion);
		const cityNames = [...new Set(regions.map(region => region.data.city))];

		return cityNames.map(cityName => ({ label: cityName }));
	}

	/**
	 * Retorna estatísticas dos endereços
	 */
	getAddressStats(): {
		totalStates: number;
		totalCities: number;
		totalRegions: number;
		totalMacroregions: number;
		statesBreakdown: Array<{
			state: string;
			cities: number;
			regions: number;
			macroregions: number;
		}>;
	} {
		const totalStates = this.addresses.length;
		let totalCities = 0;
		let totalRegions = 0;
		let totalMacroregions = 0;

		const statesBreakdown = this.addresses.map(address => {
			const citiesCount = address.cities.length;
			const regionsCount = address.regions.length;
			const macroregionsCount = address.macroregions.length;

			totalCities += citiesCount;
			totalRegions += regionsCount;
			totalMacroregions += macroregionsCount;

			return {
				state: address.state,
				cities: citiesCount,
				regions: regionsCount,
				macroregions: macroregionsCount
			};
		});

		return {
			totalStates,
			totalCities,
			totalRegions,
			totalMacroregions,
			statesBreakdown
		};
	}

	/**
	 * Busca endereços por termo de pesquisa
	 */
	searchAddresses(searchTerm: string): {
		states: string[];
		cities: Array<{ label: string; state: string }>;
		regions: Array<{
			label: string;
			city: string;
			state: string;
			macroregion: string;
		}>;
		macroregions: Array<{ label: string; state: string }>;
	} {
		const normalizedTerm = searchTerm.toLowerCase().trim();

		if (!normalizedTerm) {
			return {
				states: [],
				cities: [],
				regions: [],
				macroregions: []
			};
		}

		const states: string[] = [];
		const cities: Array<{ label: string; state: string }> = [];
		const regions: Array<{
			label: string;
			city: string;
			state: string;
			macroregion: string;
		}> = [];
		const macroregions: Array<{ label: string; state: string }> = [];

		this.addresses.forEach(address => {
			// Buscar estados
			if (address.state.toLowerCase().includes(normalizedTerm)) {
				states.push(address.state);
			}

			// Buscar cidades
			address.cities.forEach(city => {
				if (city.label.toLowerCase().includes(normalizedTerm)) {
					cities.push({
						label: city.label,
						state: address.state
					});
				}
			});

			// Buscar regiões
			address.regions.forEach(region => {
				if (
					region.label.toLowerCase().includes(normalizedTerm) ||
					region.data.city.toLowerCase().includes(normalizedTerm) ||
					region.data.macroregion.toLowerCase().includes(normalizedTerm)
				) {
					regions.push({
						label: region.label,
						city: region.data.city,
						state: address.state,
						macroregion: region.data.macroregion
					});
				}
			});

			// Buscar macro-regiões
			address.macroregions.forEach(macroregion => {
				if (macroregion.label.toLowerCase().includes(normalizedTerm)) {
					macroregions.push({
						label: macroregion.label,
						state: address.state
					});
				}
			});
		});

		return {
			states,
			cities,
			regions,
			macroregions
		};
	}

	/**
	 * Extrai informações de cidade e estado do addressSlug
	 * Segue a mesma lógica do breadcrumb para garantir consistência
	 */
	static extractCityFromAddressSlug(addressSlug: string): {
		city: string;
		state: string;
	} {
		if (!addressSlug) return { city: '', state: '' };

		// * Separator includes leading and trailing spaces to handle special cases where a city or region name has a hyphen (e.g. "Mogi-Guaçu")
		const PARTS_SEPARATOR = ' - ';

		// Split by comma and clean up
		const parts = addressSlug.split(',').map(part => part.trim());

		// Check if it's a POI (has street with hyphen in the second part)
		if (parts[1]?.includes(PARTS_SEPARATOR)) {
			// Format: "Parque Ibirapuera, Avenida Pedro Álvares Cabral - Vila Mariana, São Paulo - SP"
			// Handle city and state from the third part
			const [cityPart = '', statePart = ''] = (parts[2] || '')
				.split(PARTS_SEPARATOR)
				.map(p => p.trim());

			return { city: cityPart, state: statePart };
		}

		// Format: "Rua Tabapuã, 25, Itaim Bibi, São Paulo - SP" or "Rua Tabapuã, Itaim Bibi, São Paulo - SP"
		// In both cases, city and state are in the last part
		const cityStatePart = parts[parts.length - 1] || '';
		const [cityPart = '', statePart = ''] = cityStatePart
			.split(' - ')
			.map(p => p.trim());

		return { city: cityPart, state: statePart };
	}
}
